import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { tokenStorage } from "../lib/tokenStorage";
import { authEvents } from "../lib/authEvents";
import { useToast } from "../components/ui/Toast";
import { useScriptMode } from "./ScriptModeContext";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => tokenStorage.get());
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const { t } = useScriptMode();

  // baseQuery 401'de token'ı zaten temizlediği için, olay geldiğinde oturumun
  // önceden var olup olmadığını bu ref üzerinden anlıyoruz.
  const hadSessionRef = useRef(Boolean(token));
  hadSessionRef.current = Boolean(token);

  const setSession = useCallback(({ token: nextToken, user: nextUser }) => {
    if (nextToken) tokenStorage.set(nextToken);
    setToken(nextToken || null);
    setUser(nextUser || null);
  }, []);

  const clearSession = useCallback(() => {
    tokenStorage.clear();
    setToken(null);
    setUser(null);
  }, []);

  // Listen for global 401 events from baseQuery
  useEffect(() => {
    const off = authEvents.on("unauthorized", (payload) => {
      // Oturumu olan kullanıcıyı sessizce giriş ekranına atmıyoruz; neden
      // çıkarıldığını söylüyoruz. Girişte alınan 401'lerde uyarı gereksiz.
      if (hadSessionRef.current) {
        toast.error(payload?.message || t("error_session_expired"));
      }
      clearSession();
    });
    return off;
  }, [clearSession, toast, t]);

  const value = useMemo(
    () => ({
      token,
      user,
      isLoading,
      setIsLoading,
      hasToken: Boolean(token),
      setSession,
      clearSession,
      setUser,
    }),
    [token, user, isLoading, setSession, clearSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
